import { DiContainer } from '@jkhy/frontend-utils/di';
import { DynamicComponentExitReason } from '@jkhy/frontend-utils/lit/dynamic-component-registry';
import CompanyInstitutionOptions from '@treasury/domain/backoffice/mappings/company-configuration/company-positive-pay.js';
import { CompanyPositivePayServices } from '@treasury/domain/backoffice/requests/company-configuration/company-positive-pay.js';
import InstitutionPositivePayRequests from '@treasury/domain/backoffice/requests/institution-configuration/institution-positive-pay-requests.js';
import { CompanyProductFeatures } from '@treasury/domain/backoffice/requests/product-features/product-features.js';
import { Feature, FeatureFlagService } from '@treasury/domain/services/feature-flags';
import { TmApiError } from '@treasury/domain/shared';
import { TmDialogService } from '@treasury/presentation/services/dialog';
import { html } from 'lit';

/**
 * @typedef { Object } PosPayError
 * @property { string } posPayType
 * @property { string } accountId
 * @property { string } message
 */

/**
 * @typedef { Object } PosPayResponse
 * @property { PosPayError[] } validationErrors
 */

/**
 * @param { ng.IScope} $scope
 * @param { ng.ITimeoutService } $timeout
 * @param { ng.IQService} $q
 * @param { import('@jkhy/frontend-utils/di').DiContainer} TmDi
 */
function darkCustomizeArpController(
    $scope,
    $state,
    $stateParams,
    toaster,
    $timeout,
    entitlementsService,
    $q,
    TmDi,
    modalService
) {
    function hasEntitlement(entitlement) {
        return entitlementsService.hasEntitlement(entitlement);
    }

    function setUpCheck() {
        if ($scope.isCheckVisible) {
            $scope.checkEnabled = $scope.posPay.areCheckExceptionsEnabled();
            $scope.checkAccounts = $scope.posPay.getCheckExceptions();
        }
    }

    function setUpAch() {
        if ($scope.isACHVisible) {
            $scope.workAchException = $scope.posPay.areAchExceptionsEnabled();
            $scope.achExceptionAccounts = $scope.posPay.getAchExceptions();
            $scope.approveCount = $scope.posPay.getAchApprovers();
        }
    }

    async function isFiAchFilterEnabled() {
        const options = $scope.posPay.institutionOptions.institutionConfig;
        const isAllowManageAchFilters =
            options['PositivePay.ACHFilterManagement.AllowManageAchFilters'] === '1';
        const hasACHFilterManagement = await hasEntitlement(
            'Feature.PositivePay.ACHFilterManagement'
        );
        // Can't check saved state of options. They want to toggle the visibility in
        // the temporary state of the form in the UI.
        const workExceptionsAreOn =
            $scope.posPay.companyFeatures.companyConfiguration['PositivePay.WorkAchExceptions'] ===
            '1';
        return hasACHFilterManagement && isAllowManageAchFilters && workExceptionsAreOn;
    }

    function setUpAchFilterRules() {
        if ($scope.isACHFiltersVisible) {
            $scope.showFilters = $scope.posPay.areAchFiltersEnabled();
            $scope.achFilterAccounts = $scope.posPay.getAchFilterAccounts();
            $scope.approveCount = $scope.posPay.getAchApprovers();
        }
    }

    function setUpSubstituteProduct() {
        /*
        All config will come out of inst options API
        not sure about the contract since its not set up for the FI the ff is on
        Tony is not sure product wants to even support this, 
        so need to get yes/no before dealing with this headache...stubbing for now        
       */
        $scope.isSubstituteProductVisible = $scope.posPay.isSubstituteProductVisible();
    }

    function setUpIssuedItems() {
        $scope.areIssuedItemsVisible = $scope.posPay.areIssuedItemsVisible();
        if ($scope.areIssuedItemsVisible) {
            $scope.issuedEnabled = $scope.posPay.isIssuedItemsEnabled();
        }
    }

    function setUpIssuedItemsModification() {
        if ($scope.hasIssuedItemsModificationFeature) {
            $scope.allowIssuedItemsEdit = $scope.posPay.isIssuedItemsEditEnabled();
            $scope.allowIssuedItemsDelete = $scope.posPay.isIssuedItemsDeleteEnabled();
        }
    }

    function setUpActivityReview() {
        $scope.isAllowCorrectionRequestVisible = $scope.posPay.isAllowCorrectionRequestVisible();
        if ($scope.isAllowCorrectionRequestVisible) {
            $scope.allowCorrectionRequest = $scope.posPay.isAllowCorrectionRequestEnabled();
        }
        $scope.isAllowReturnReasonVisible = $scope.posPay.isAllowReturnReasonVisible();
        if ($scope.isAllowReturnReasonVisible) {
            $scope.allowReturnReason = $scope.posPay.isAllowReturnReasonEnabled();
        }
    }

    function isAchInFocus() {
        return $scope.isACHVisible && $scope.activeTab === 'ach';
    }

    function isAchFiltersInFocus() {
        return $scope.isACHFiltersVisible && $scope.activeTab === 'ach-filters';
    }

    function isCheckInFocus() {
        return $scope.isCheckVisible && $scope.activeTab === 'check';
    }

    async function update(shouldSwitchTabs = false) {
        if ($scope.posPay.hasLoaded) {
            $q.resolve(isFiAchFilterEnabled()).then(isACHFiltersVisible => {
                $scope.isACHFiltersVisible = isACHFiltersVisible;
                $scope.isCheckVisible = $scope.posPay.areCheckExceptionsVisible();
                $scope.isACHVisible = $scope.posPay.areAchExceptionsVisible();
                setUpIssuedItems();
                setUpIssuedItemsModification();
                setUpSubstituteProduct();
                setUpActivityReview();
                $scope.hasChanged = $scope.posPay.hasChanged();
                if (!shouldSwitchTabs) {
                    setInitialTabs();
                }
                setUpTab($scope.activeTab);
            });
        } else {
            $timeout(() => {
                update();
            }, 1000);
        }
    }

    function setInitialTabs() {
        if ($scope.isCheckVisible) {
            setActiveTab('check');
        } else if ($scope.isACHVisible) {
            setActiveTab('ach');
        } else if ($scope.isACHFiltersVisible) {
            setActiveTab('ach-filters');
        }
    }

    function setUpTab(tab) {
        switch (tab) {
            case 'check':
                setUpCheck();
                break;
            case 'ach':
                setUpAch();
                setUpAchFilterRules();
                break;
            case 'ach-filters':
                setUpAchFilterRules();
                break;
            default:
                throw new Error('Invalid tab name');
        }
    }

    async function setActiveTab(tab) {
        $scope.customError = null;
        $scope.activeTab = tab;
        if ($scope.posPay.companyAccounts.isPerformanceEnabled) {
            $scope.posPay.companyAccounts.resetTab(tab);
            setUpTab(tab);
            if (
                tab === 'check' &&
                $scope.posPay.getCheckExceptions() &&
                $scope.posPay.getCheckExceptions().length > 0
            ) {
                return;
            }
            if (
                tab === 'ach' &&
                $scope.posPay.getAchExceptions() &&
                $scope.posPay.getAchExceptions().length > 0
            ) {
                return;
            }
            if (
                tab === 'ach-filters' &&
                $scope.posPay.getAchFilterAccounts() &&
                $scope.posPay.getAchFilterAccounts().length > 0
            ) {
                return;
            }
            const promise = $scope.posPay.companyAccounts.fetchExceptions(tab);
            $q.resolve(promise)
                .catch(error => handleApiError(error, tab))
                .finally(() => setUpTab(tab));
        } else {
            setUpTab(tab);
        }
    }

    function handleApiError(error, tab) {
        let errorString = error.toString();
        if (errorString.startsWith('Error: ')) {
            errorString = errorString.substring(7);
        }
        const jsonPart = errorString.split('at TmHttpClientMfa.transformBadResponse')[0].trim();
        if (!jsonPart) {
            console.error('Failed to extract JSON from error:', error);
            return;
        }
        try {
            const errorObject = JSON.parse(jsonPart);
            const responseDetail = errorObject.responseDetailCollection?.[0];
            if (responseDetail) {
                setCustomError(tab, responseDetail);
            } else {
                console.error('Error does not contain responseDetailCollection');
            }
        } catch (parseError) {
            console.error('Failed to parse error JSON:', parseError);
        }
    }

    function setCustomError(tab, responseDetail) {
        $scope.customError = {};
        $scope.customError.code = responseDetail.responseCode;
        $scope.customError.message = responseDetail.responseMessage;
        switch (tab) {
            case 'check':
                $scope.customError.accountType = 'Check Exception Accounts';
                break;
            case 'ach':
                $scope.customError.accountType = 'ACH Exception Accounts';
                break;
            case 'ach-filters':
                $scope.customError.accountType = 'ACH Exception - Filter Rules Accounts';
                break;
            default:
                throw new Error('Invalid tab name');
        }
        const summaryText = `An error occurred retrieving ${$scope.customError.accountType}.
                             Code ${$scope.customError.code}`;
        const modalOptions = {
            isAlert: true,
            alertType: 'Error',
            headerText: 'Error',
            summaryText: summaryText,
            time: new Date().toLocaleString('en-US', { timeZoneName: 'short' }),
        };
        modalService.showModal({}, modalOptions);
    }

    function handleAddAccount(account) {
        function getAccountType() {
            switch ($scope.activeTab) {
                case 'ach':
                    return 'WorkACHExceptions';
                case 'ach-filters':
                    return 'ACHFilterManagement';
                default:
                    return 'WorkCheckExceptions';
            }
        }

        const accountType = getAccountType();
        $scope.posPay.companyAccounts.changeAccountsOnSave(accountType, account);
        $scope.save();
    }

    async function init() {
        const ffService = await TmDi.getAsync(FeatureFlagService);
        const companyId = $stateParams.id;
        Promise.all([
            ffService.isEnabled(Feature.IssuedItemsModification),
            CompanyProductFeatures.hasPositivePay(companyId),
            CompanyPositivePayServices.getCompany(companyId),
        ]).then(response => {
            const [hasIssuedItemsModification, productIsAlreadyEnabled, company] = response;
            $scope.hasIssuedItemsModificationFeature = hasIssuedItemsModification;
            $scope.requiredEntitlement = productIsAlreadyEnabled;
            $scope.posPay = new CompanyInstitutionOptions(
                company,
                ffService,
                hasIssuedItemsModification
            );
            $scope.companyName = company.name;
            update();
        });
    }

    async function toggleFeature(key) {
        $scope.addArpProductForm.$setDirty();
        await $scope.posPay.toggle(key);
        update(true);
    }

    function changeApprovers(newValue) {
        $scope.posPay.updateAchApprovers(newValue);
        update();
    }

    function toggleAccount(key, accountId, warningMessage) {
        $scope.posPay.updateAccount(key, accountId);
        $scope[`${key}Warnings`][accountId] = warningMessage;
    }

    function requiredEntitlements() {
        return $scope.requiredEntitlement === true ? 'Edit Product Feature' : 'Add Product Feature';
    }

    init();

    function validateSaveResponse(response) {
        if (response.validationErrors) {
            return response.validationErrors.length === 0;
        }
        if (response.responseDetails) {
            return response.responseDetails.length === 0;
        }
        return false;
    }

    async function getDialogService() {
        /**
         * Intentionally casting this to `OmegaDialogService` to
         * allow access to the last `options` parameter in `open()` method for legacy config.
         *
         * @type {import('@treasury/omega/services/omega-dialog').OmegaDialogService}
         */
        const dialogService = TmDi.get(TmDialogService);
        return dialogService;
    }

    /**
     *
     * @param { boolean } valid
     * @param { string | import('lit').TemplateResult} message
     */
    async function showSaveErrorDialog(valid, message) {
        const messageType = valid ? 'completed but' : 'failed';
        const dialogService = await getDialogService();
        const result = dialogService.open(
            html`The configuration save operation ${messageType} with the following validation
                errors:<br />${message}`,
            'Positive Pay Configuration',
            {
                buttons: [
                    {
                        reason: DynamicComponentExitReason.Confirm,
                        label: 'Ok',
                    },
                ],
            },
            {
                maxWidth: 450,
            }
        );
        return $q.resolve(result);
    }

    function getTypeMapping(posPayType) {
        switch (posPayType) {
            case 'AchFilterManagement':
                return 'achFilterException';
            case 'CheckExceptions':
                return 'checkException';
            case 'AchExceptions':
                return 'achException';
            default:
                return '';
        }
    }

    /**
     * @param { TmApiError | PosPayResponse  } response
     */
    function handleInvalidSaveResponse(response) {
        if (response instanceof TmApiError) {
            const subErrors = response.subErrors || [];
            const message = subErrors.map(
                error => html`<br /><b>${error.responseCode}:</b> ${error.responseMessage}`
            );

            return showSaveErrorDialog(false, message);
        }

        if (response.validationErrors) {
            response.validationErrors.forEach(error => {
                toggleAccount(getTypeMapping(error.posPayType), error.accountId, error.message);
            });
        } else {
            return showSaveErrorDialog(false, 'An unknown error occurred.');
        }
    }

    function save() {
        $scope.isLoading = true;
        try {
            $q.resolve($scope.posPay.save()).then(response => {
                if (validateSaveResponse(response)) {
                    toaster.save('Positive Pay');
                } else {
                    handleInvalidSaveResponse(response);
                }
                $q.resolve(update(true)).then(() => {
                    $scope.addArpProductForm.$setPristine();
                    $scope.isLoading = false;
                });
            });
        } catch (e) {
            handleInvalidSaveResponse(e);
        }
    }

    function goBack() {
        $state.go('company', { id: $stateParams.id });
    }

    $scope.isAchInFocus = isAchInFocus;
    $scope.isCheckInFocus = isCheckInFocus;
    $scope.isAchFiltersInFocus = isAchFiltersInFocus;
    $scope.goBack = goBack;
    $scope.save = save;
    $scope.toggleFeature = toggleFeature;
    $scope.requiredEntitlements = requiredEntitlements;
    $scope.setActiveTab = setActiveTab;
    $scope.handleAddAccount = handleAddAccount;
    $scope.approvers = ['None', '1', '2', '3'];
    $scope.isCheckVisible = false;
    $scope.isACHVisible = false;
    $scope.isACHFiltersVisible = false;
    $scope.isSubstituteProductVisible = false;
    $scope.areIssuedItemsVisible = false;
    $scope.isAllowCorrectionRequestEnabled = false;
    $scope.isAllowReturnReasonEnabled = false;
    $scope.changeApprovers = changeApprovers;
    $scope.allowIssuedItemsActivityInfo =
        'Activation of this configuration will allow users to review the Issued Items Activity listing.';
    $scope.allowIssuedItemsEditInfo =
        'Users will have the option to edit the check number, issued date, amount, or payee for outstanding issued items.';
    $scope.allowIssuedItemsDeleteInfo =
        'If an outstanding issued item is deleted, it will be removed from the issued item listing within SilverLake but will remain visible within the user’s Issued Items Activity page with a deleted status.';
    $scope.hasAchFilterManagementEntitlement = hasEntitlement(
        'Feature.PositivePay.ACHFilterManagement'
    );
    $scope.hasReturnReasonEntitlement = hasEntitlement('Feature.PositivePay.ReturnReason');
    $scope.isLoading = false;
    $scope.checkExceptionWarnings = {};
    $scope.achExceptionWarnings = {};
    $scope.achFilterExceptionWarnings = {};
}

window.angular
    .module('backOffice')
    .controller('darkCustomizeArpController', darkCustomizeArpController);

darkCustomizeArpController.$inject = [
    '$scope',
    '$state',
    '$stateParams',
    'toaster',
    '$timeout',
    'entitlementsService',
    '$q',
    'TmDi',
    'modalService',
];
